import { z } from 'zod';
import type { EventType } from './events';

/*
 * Webhook dispatch (S-08-07; ADR-0019 §6). One `webhook_delivery` row per attempt; the
 * dispatcher signs the raw JSON body with HMAC-SHA256 of the subscription secret and retries
 * on the fixed schedule below, swept through `webhook_delivery.next_attempt_at`.
 */

export const WEBHOOK_DELIVERY_STATUSES = ['pending', 'delivered', 'failed', 'dead'] as const;
export const WebhookDeliveryStatusSchema = z.enum(WEBHOOK_DELIVERY_STATUSES);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatusSchema>;

/** Wait before attempt 2, 3, 4, 5 and 6; after the sixth failure the delivery is `dead`. */
export const WEBHOOK_RETRY_SCHEDULE_MINUTES = [1, 5, 30, 120, 720] as const;
export const WEBHOOK_MAX_ATTEMPTS = WEBHOOK_RETRY_SCHEDULE_MINUTES.length + 1;
/** Dead deliveries in a row that disable the subscription (`webhook_subscription.disabled_reason`). */
export const WEBHOOK_MAX_CONSECUTIVE_FAILURES = 10;
export const WEBHOOK_TIMEOUT_MS = 10_000;

export const SIGNATURE_HEADER = 'x-servicetim-signature';
export const EVENT_ID_HEADER = 'x-servicetim-event-id';
/** Header value: `sha256=<hex hmac of the raw body>`. */
export const SIGNATURE_PREFIX = 'sha256=';

/** Delivery-log events are never themselves delivered (they would chain forever). */
export const WEBHOOK_EXCLUDED_EVENT_TYPES = [
  'webhook.delivered',
  'webhook.dead',
] as const satisfies readonly EventType[];

/**
 * Minutes to wait after `failedAttempt` (1-based) before the next try, or null when the
 * schedule is exhausted and the delivery becomes `dead`.
 */
export function nextRetryDelayMinutes(failedAttempt: number): number | null {
  return WEBHOOK_RETRY_SCHEDULE_MINUTES[failedAttempt - 1] ?? null;
}

/** Item of `GET /admin/v1/webhooks/{id}/deliveries`; `requestBody` is the posted envelope. */
export const WebhookDeliverySchema = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  eventId: z.uuid(),
  /** `requestBody.type`, lifted for lists and filters. */
  eventType: z.string().min(1),
  attempt: z.int().min(1),
  status: WebhookDeliveryStatusSchema,
  responseStatus: z.int().nullable(),
  error: z.string().nullable(),
  nextAttemptAt: z.iso.datetime().nullable(),
  deliveredAt: z.iso.datetime().nullable(),
  requestBody: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

export const WebhookDeliveryListQuerySchema = z.object({
  status: WebhookDeliveryStatusSchema.optional(),
});
export type WebhookDeliveryListQuery = z.infer<typeof WebhookDeliveryListQuerySchema>;

export const WebhookDeliveryListResponseSchema = z.object({
  items: z.array(WebhookDeliverySchema),
  nextCursor: z.string().nullable(),
});
export type WebhookDeliveryListResponse = z.infer<typeof WebhookDeliveryListResponseSchema>;
